/**
 * The open month, live from bank alerts. Provisional and labelled so: it says which
 * accounts it covers, which it does not, and when the last alert arrived. Closing the
 * month replaces it with statement truth.
 */
import type { Month } from '../data/types';
import { useApp } from './context';
import { useAsync } from './hooks';
import { Empty, Panel, Stat } from './ui';
import { monthSoFar } from '../lib/sofar';
import { DASH, money, naira, nairaShort } from '../lib/format';

const monthName = (start: string) => new Date(`${start}T00:00:00`).toLocaleDateString('en-GB', { month: 'long' });
const when = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Lagos' });
const list = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

function PlanOnly({ plan, note }: { plan: Month; note?: string }) {
  return (
    <Panel title={`${monthName(plan.month_start)} plan`} meta="No actuals until it is closed">
      <div className="stats-3">
        <Stat label="Invest first" value={nairaShort(plan.investment_target)} foot="Target" />
        <Stat label="Personal spend" value={nairaShort(plan.personal_spend_cap)} foot="Ceiling" />
        <Stat label="Lifestyle" value={nairaShort(plan.lifestyle_spend_cap)} foot="Ceiling" />
      </div>
      {note && <p className="hero-note sofar-note">{note}</p>}
    </Panel>
  );
}

export function SoFar({ plan }: { plan: Month }) {
  const { repo, base } = useApp();
  const data = useAsync(async () => {
    const rows = await repo.provisional(plan.month_start);
    return { rows, rules: rows && rows.length ? await repo.payeeRules() : [] };
  }, [plan.id]);

  if (!data.data || data.data.rows === null) return <PlanOnly plan={plan} />;
  const { rows, rules } = data.data;
  const banks = [...new Set(base.accounts.filter((a) => a.alert_bank).map((a) => a.alert_bank as string))].sort();
  if (rows.length === 0) {
    return <PlanOnly plan={plan} note={banks.length ? `No ${list(banks)} alerts have arrived yet this month.` : undefined} />;
  }

  const s = monthSoFar(rows, base.categories, rules, plan, base.accounts);
  const cap = plan.personal_spend_cap;
  const pct = cap ? Math.min(100, (s.money.personal / cap) * 100) : 0;
  const over = s.ceilingLeft !== null && s.ceilingLeft < 0;
  const name = monthName(plan.month_start);

  return (
    <Panel title={`${name} so far`} meta={<span className="tag tag-watch">Provisional</span>}>
      <div className="stats-3">
        <Stat label="Personal spend" value={nairaShort(s.money.personal)} foot={cap ? `of ${nairaShort(cap)} ceiling` : 'No ceiling set'} />
        <Stat label={over ? 'Over the ceiling' : 'Left to spend'} value={s.ceilingLeft === null ? DASH : nairaShort(Math.abs(s.ceilingLeft))}
          foot={s.perDayLeft === null || over ? (over ? 'Already past it' : 'Set a ceiling in the plan') : `about ${nairaShort(s.perDayLeft)} a day for ${s.daysLeft} days`} />
        <Stat label="Invested so far" value={nairaShort(s.money.invested)} foot={plan.investment_target ? `target ${nairaShort(plan.investment_target)}` : 'No target set'} />
      </div>
      {cap ? <span className="meter" role="img" aria-label={`Personal spend: ${Math.round(pct)}% of the ceiling`}><i style={{ width: `${pct}%`, background: over ? 'var(--over)' : pct > 80 ? 'var(--watch)' : 'var(--within)' }} /></span> : null}
      {s.topCategories.length > 0 && (
        <ul className="ledger sofar-ledger">
          {s.topCategories.map((c) => <li key={c.name}><span>{c.name}</span><b>{naira(c.amount)}</b></li>)}
          {s.fromInvestments > 0 && <li><span>Taken out of investments<small>Redemptions, not income</small></span><b>{naira(s.fromInvestments)}</b></li>}
          {s.money.business > 0 && <li><span>Migrate World<small>Business capital, not personal spend</small></span><b>{naira(s.money.business)}</b></li>}
        </ul>
      )}
      <p className="hero-note sofar-note">
        From {s.rows} {list(s.banksSeen)} alert{s.rows === 1 ? '' : 's'}{s.lastSeen ? `, last at ${when(s.lastSeen)}` : ''}.
        {s.notCovered.length > 0 && <> No alerts yet this month from {list(s.notCovered)}.</>}
        {' '}Confirmed when you close {name}.
      </p>
      <details className="sofar-latest">
        <summary>All alerts this month ({s.txs.length})</summary>
        <ul className="ledger">
          {s.txs.map((t) => (
            <li key={t.id}>
              <span>{t.description}<small>{when(rows.find((r) => r.id === t.id)!.occurred_at)} · {t.category_name ?? 'Unsorted'}</small></span>
              <b className={t.direction === 'credit' ? 'credit' : ''}>{t.direction === 'credit' ? '+' : ''}{money(t.amount, t.currency)}</b>
            </li>
          ))}
        </ul>
      </details>
    </Panel>
  );
}
